import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Factory, Loader2, Plus, Trash2, PlayCircle, Scale, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(s) {
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}

function calcularPesoEstimadoKg(produto, numeroBateladas) {
  const nb = Number(numeroBateladas) || 0;
  if (nb <= 0) return null;
  const form = produto?.formulacao;
  if (!Array.isArray(form) || form.length === 0) return null;
  const total = form.reduce((s, item) => {
    const min = Number(item.quantidade_minima) * nb;
    const max = Number(item.quantidade_maxima) * nb;
    if (Number.isFinite(min) && Number.isFinite(max)) return s + (min + max) / 2;
    if (Number.isFinite(min)) return s + min;
    return s;
  }, 0);
  return total > 0 ? Math.round(total * 1000) / 1000 : null;
}

function formatarPeso(kg, unidade = 'kg') {
  return formatQuantidadeComUnidade(kg, unidade || 'kg', '—');
}

const linhaVazia = () => ({ produto_id: '', numero_bateladas: 1 });

const PlanejamentoPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [cursorMonth, setCursorMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => toYmd(new Date()));
  const [items, setItems] = useState([linhaVazia()]);
  const [planItens, setPlanItens] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const produtoPorId = useMemo(() => {
    const map = new Map();
    produtos.forEach((p) => map.set(p.id, p));
    return map;
  }, [produtos]);

  const enriquecerItem = useCallback(
    (it, idx) => {
      const produto = produtoPorId.get(it.produto_id);
      const nb = Number(it.numero_bateladas) || 0;
      const pesoKg = calcularPesoEstimadoKg(produto, nb);
      const unidade = produto?.formulacao?.[0]?.unidade || 'kg';
      return {
        ...it,
        _key: `${it.produto_id}-${idx}-${nb}`,
        produto_nome: produto?.nome_produto || it.produto_nome || 'Produto',
        peso_kg: pesoKg,
        unidade
      };
    },
    [produtoPorId]
  );

  const itensEnriquecidos = useMemo(
    () => planItens.map((it, idx) => enriquecerItem(it, idx)),
    [planItens, enriquecerItem]
  );

  const resumoPorProduto = useMemo(() => {
    const map = new Map();
    itensEnriquecidos.forEach((it) => {
      const id = it.produto_id;
      if (!id) return;
      const cur = map.get(id) || {
        produto_id: id,
        produto_nome: it.produto_nome,
        peso_total_kg: 0,
        total_bateladas: 0,
        linhas: 0,
        unidade: it.unidade || 'kg'
      };
      cur.peso_total_kg += Number(it.peso_kg) || 0;
      cur.total_bateladas += Number(it.numero_bateladas) || 0;
      cur.linhas += 1;
      map.set(id, cur);
    });
    return Array.from(map.values()).map((r) => ({
      ...r,
      peso_total_kg: Math.round(r.peso_total_kg * 1000) / 1000
    }));
  }, [itensEnriquecidos]);

  const pesoTotalDia = useMemo(
    () =>
      Math.round(
        itensEnriquecidos.reduce((s, it) => s + (Number(it.peso_kg) || 0), 0) * 1000
      ) / 1000,
    [itensEnriquecidos]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listProductionProducts = httpsCallable(functions, 'listProductionProducts');
        const r = await listProductionProducts({
          empresa_id: getEmpresaId(),
          apenas_ativos: true,
          limit: 500
        });
        if (!cancelled && r.data?.success) {
          setProdutos(r.data.data?.items || []);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getEmpresaId]);

  const carregarPlanejamentoDia = useCallback(async () => {
    setLoadingPlan(true);
    setErr('');
    try {
      const getPlanejamentoProducao = httpsCallable(functions, 'getPlanejamentoProducao');
      const r = await getPlanejamentoProducao({
        empresa_id: getEmpresaId(),
        data_planejada: selectedDate
      });
      const plan = r.data?.data;
      setPlanItens(Array.isArray(plan?.itens) ? plan.itens : []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar planejamento');
      setPlanItens([]);
    } finally {
      setLoadingPlan(false);
    }
  }, [selectedDate, getEmpresaId]);

  useEffect(() => {
    setItems([linhaVazia()]);
    carregarPlanejamentoDia();
  }, [carregarPlanejamentoDia]);

  const calendarCells = useMemo(() => {
    const y = cursorMonth.getFullYear();
    const m = cursorMonth.getMonth();
    const first = new Date(y, m, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [cursorMonth]);

  const addLinha = () => {
    setItems([...items, linhaVazia()]);
  };

  const removeLinha = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateLinha = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };

  const inserirNoPlanejamento = async () => {
    setErr('');
    setMsg('');
    const clean = items.filter((x) => x.produto_id && Number(x.numero_bateladas) > 0);
    if (clean.length === 0) {
      setErr('Informe ao menos um produto e número de bateladas.');
      return;
    }
    try {
      setLoading(true);
      const salvarPlanejamentoProducao = httpsCallable(functions, 'salvarPlanejamentoProducao');
      const r = await salvarPlanejamentoProducao({
        empresa_id: getEmpresaId(),
        data_planejada: selectedDate,
        itens: clean.map((it) => ({
          produto_id: it.produto_id,
          numero_bateladas: Number(it.numero_bateladas)
        }))
      });
      const inseridos = r.data?.data?.inseridos ?? clean.length;
      setMsg(
        inseridos === 1
          ? '1 ordem inserida no planejamento deste dia.'
          : `${inseridos} ordens inseridas no planejamento deste dia.`
      );
      setItems([linhaVazia()]);
      await carregarPlanejamentoDia();
    } catch (e) {
      setErr(e.message || 'Erro ao inserir no planejamento');
    } finally {
      setLoading(false);
    }
  };

  const gerarOrdens = async () => {
    if (planItens.length === 0) {
      setErr('Não há itens no planejamento desta data para gerar ordens.');
      return;
    }
    if (!window.confirm('Gerar ordens de produção a partir deste planejamento?')) return;
    setErr('');
    setMsg('');
    try {
      setLoading(true);
      const gerarOrdensDoPlanejamento = httpsCallable(functions, 'gerarOrdensDoPlanejamento');
      const r = await gerarOrdensDoPlanejamento({
        empresa_id: getEmpresaId(),
        data_planejada: selectedDate
      });
      const total = r.data?.data?.total ?? 0;
      const erros = r.data?.data?.erros || [];
      setMsg(`Foram criadas ${total} ordem(ns).${erros.length ? ` Avisos: ${erros.join('; ')}` : ''}`);
    } catch (e) {
      setErr(e.message || 'Erro ao gerar ordens');
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('manage_production')) {
    return (
      <div className="p-6 text-center text-gray-600">
        Sem permissão para planejamento de produção.
      </div>
    );
  }

  const dataLabel = parseYmd(selectedDate).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="h-7 w-7 text-blue-600" />
          Planejamento de Produção
        </h1>
        <p className="text-gray-600 mt-1">
          Selecione o dia no calendário, insira novas ordens no planejamento e gere as ordens de produção quando
          estiver pronto.
        </p>
      </div>

      {err && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{err}</div>
      )}
      {msg && (
        <div className="rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">{msg}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              className="px-2 py-1 rounded border text-sm"
              onClick={() =>
                setCursorMonth(new Date(cursorMonth.getFullYear(), cursorMonth.getMonth() - 1, 1))
              }
            >
              ←
            </button>
            <span className="font-semibold text-gray-900 capitalize">
              {cursorMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              className="px-2 py-1 rounded border text-sm"
              onClick={() =>
                setCursorMonth(new Date(cursorMonth.getFullYear(), cursorMonth.getMonth() + 1, 1))
              }
            >
              →
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-2">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((cell, i) => {
              if (!cell) return <div key={`e-${i}`} className="h-10" />;
              const key = toYmd(cell);
              const sel = key === selectedDate;
              const isToday = key === toYmd(new Date());
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDate(key)}
                  className={`h-10 rounded-lg text-sm transition-colors ${
                    sel
                      ? 'bg-blue-600 text-white font-semibold'
                      : isToday
                      ? 'bg-blue-50 text-blue-800 border border-blue-200'
                      : 'hover:bg-gray-100 text-gray-800'
                  }`}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Dia selecionado: <strong>{dataLabel}</strong>
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Factory className="h-5 w-5" />
            Adicionar Ordem de Produção
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            Preencha produto e bateladas e clique em Inserir no Planejamento. Os itens já programados aparecem na lista
            abaixo.
          </p>
          <div className="space-y-3">
            {items.map((row, idx) => (
              <div key={idx} className="flex flex-wrap gap-2 items-end border-b border-gray-100 pb-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 mb-1">Produto</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={row.produto_id}
                    onChange={(e) => updateLinha(idx, 'produto_id', e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {produtos.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.codigo ? `${p.codigo} — ` : ''}
                        {p.nome_produto}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-28">
                  <label className="block text-xs text-gray-500 mb-1">Bateladas</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={row.numero_bateladas}
                    onChange={(e) => updateLinha(idx, 'numero_bateladas', parseInt(e.target.value, 10) || 1)}
                  />
                </div>
                {row.produto_id && (
                  <div className="w-full text-xs text-gray-500">
                    Peso estimado:{' '}
                    <span className="font-medium text-gray-700">
                      {formatarPeso(
                        calcularPesoEstimadoKg(produtoPorId.get(row.produto_id), row.numero_bateladas),
                        produtoPorId.get(row.produto_id)?.formulacao?.[0]?.unidade
                      )}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  onClick={() => removeLinha(idx)}
                  disabled={items.length <= 1}
                  title="Remover linha"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addLinha}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus className="h-4 w-4" />
              Adicionar outro produto
            </button>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              type="button"
              disabled={loading}
              onClick={inserirNoPlanejamento}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Inserir no Planejamento
            </button>
            <button
              type="button"
              disabled={loading || planItens.length === 0}
              onClick={gerarOrdens}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <PlayCircle className="h-4 w-4" />
              Gerar ordens automaticamente
            </button>
          </div>
        </div>
      </div>

      {/* Ordens já inseridas no dia */}
      <section className="bg-white rounded-xl shadow-sm border p-4 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Ordens programadas no dia</h2>
            <p className="text-sm text-gray-600 mt-1 capitalize">{dataLabel}</p>
          </div>
          {itensEnriquecidos.length > 0 && (
            <div className="text-sm text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">
              <span className="font-medium text-gray-900">{itensEnriquecidos.length}</span> linha(s) · Peso total do
              dia:{' '}
              <span className="font-semibold text-blue-700">
                {formatarPeso(pesoTotalDia, itensEnriquecidos[0]?.unidade)}
              </span>
            </div>
          )}
        </div>

        {loadingPlan ? (
          <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
            <Loader2 className="h-6 w-6 animate-spin" /> Carregando planejamento…
          </div>
        ) : itensEnriquecidos.length === 0 ? (
          <div className="text-center py-12 text-gray-500 border border-dashed rounded-lg">
            <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>Nenhuma ordem inserida nesta data.</p>
            <p className="text-sm mt-1">Use o formulário acima para adicionar ao planejamento.</p>
          </div>
        ) : (
          <>
            {resumoPorProduto.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6">
                {resumoPorProduto.map((card) => (
                  <div
                    key={card.produto_id}
                    className="rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm"
                  >
                    <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{card.produto_nome}</p>
                    <p className="mt-2 flex items-center gap-1.5 text-blue-800">
                      <Scale className="h-4 w-4 shrink-0" />
                      <span className="text-lg font-bold">{formatarPeso(card.peso_total_kg, card.unidade)}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {card.linhas} {card.linhas === 1 ? 'ordem' : 'ordens'} · {card.total_bateladas} batelada(s)
                    </p>
                  </div>
                ))}
              </div>
            )}

            <ul className="divide-y divide-gray-100 border rounded-lg overflow-hidden">
              {itensEnriquecidos.map((it, idx) => (
                <li
                  key={it._key}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{it.produto_nome}</p>
                    <p className="text-sm text-gray-500">
                      {it.numero_bateladas} batelada(s) · Peso estimado:{' '}
                      <span className="font-medium text-gray-700">{formatarPeso(it.peso_kg, it.unidade)}</span>
                    </p>
                  </div>
                  <span className="text-xs font-mono text-gray-400">#{idx + 1}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="text-xs text-gray-500 mt-4">
          Ordens geradas ficam como planejadas, com rastreio por carrinho (lote AAAADDD-NN). Conclua separação e pesagem
          antes de iniciar o Tempo Real.
        </p>
      </section>
    </div>
  );
};

export default PlanejamentoPage;
